import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ContentStatus } from '@cookspace-shared-contracts/core';

import { useGetModerationQueueQuery } from '@/api/moderationApi';
import { Select } from '@/components/Select';
import type { ModerationContentType } from '@/lib/moderationSchemas';

const TYPE_OPTIONS: Array<{ value: ModerationContentType; label: string }> = [
    { value: 'recipe', label: 'Рецепт' },
    { value: 'article', label: 'Статья' },
    { value: 'course', label: 'Курс' },
    { value: 'stream', label: 'Стрим' },
    { value: 'event', label: 'Мероприятие' }
];

const STATUS_OPTIONS: Array<{ value: Extract<ContentStatus, 'pending' | 'rejected'>; label: string }> = [
    { value: 'pending', label: 'На модерации' },
    { value: 'rejected', label: 'Отклонено' }
];

const TYPE_LABEL: Record<ModerationContentType, string> = {
    recipe: 'Рецепт',
    article: 'Статья',
    course: 'Курс',
    stream: 'Стрим',
    event: 'Мероприятие'
};

const STATUS_LABEL: Record<ContentStatus, string> = {
    draft: 'Черновик',
    pending: 'На модерации',
    published: 'Опубликовано',
    rejected: 'Отклонено'
};

const STATUS_COLOR: Record<ContentStatus, string> = {
    draft: 'bg-gray-100 text-gray-700',
    pending: 'bg-yellow-100 text-yellow-800',
    published: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800'
};

function formatDate(iso: string): string {
    return new Date(iso).toLocaleString('ru-RU');
}

export function ModerationPage(): ReactElement {
    const navigate = useNavigate();
    const [filterType, setFilterType] = useState<ModerationContentType | ''>('');
    const [filterStatus, setFilterStatus] = useState<'pending' | 'rejected'>('pending');

    const queryArg = useMemo(
        () => ({
            ...(filterType ? { type: filterType } : {}),
            status: filterStatus
        }),
        [filterType, filterStatus]
    );

    const { data, isLoading, isError } = useGetModerationQueueQuery(queryArg);

    const items = data?.items ?? [];

    const openItem = (type: ModerationContentType, id: string): void => {
        navigate(`/moderation/${type}/${id}`);
    };

    return (
        <div data-testid="moderation-page" className="space-y-4">
            <div className="flex items-center justify-between">
                <h1 className="text-2xl font-semibold text-gray-900">Модерация</h1>
            </div>

            <div className="flex flex-wrap items-center gap-4 rounded border border-gray-200 bg-white p-3">
                <div className="w-56">
                    <Select
                        label="Тип"
                        value={filterType}
                        onChange={(e) =>
                            setFilterType(e.target.value as ModerationContentType | '')
                        }
                        placeholder="Все типы"
                        options={TYPE_OPTIONS}
                        data-testid="moderation-filter-type"
                    />
                </div>
                <div className="w-56">
                    <Select
                        label="Статус"
                        value={filterStatus}
                        onChange={(e) =>
                            setFilterStatus(e.target.value as 'pending' | 'rejected')
                        }
                        options={STATUS_OPTIONS}
                        data-testid="moderation-filter-status"
                    />
                </div>
            </div>

            {isLoading && <div className="text-sm text-gray-500">Загрузка...</div>}
            {isError && (
                <div className="text-sm text-red-600" data-testid="moderation-error">
                    Не удалось загрузить очередь модерации
                </div>
            )}

            {!isLoading && !isError && (
                <div
                    className="overflow-hidden rounded border border-gray-200 bg-white"
                    data-testid="moderation-table"
                >
                    <div className="grid grid-cols-[80px_100px_minmax(0,2fr)_minmax(0,1fr)_140px_120px_120px] gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium uppercase text-gray-600">
                        <span>ID</span>
                        <span>Тип</span>
                        <span>Заголовок</span>
                        <span>Автор</span>
                        <span>Создано</span>
                        <span>Статус</span>
                        <span className="text-right">Действия</span>
                    </div>

                    {items.length === 0 && (
                        <div className="px-3 py-6 text-center text-sm text-gray-500">
                            Очередь пуста
                        </div>
                    )}

                    {items.map((item) => (
                        <div
                            key={`${item.type}-${item.id}`}
                            className="grid grid-cols-[80px_100px_minmax(0,2fr)_minmax(0,1fr)_140px_120px_120px] items-center gap-2 border-b border-gray-100 px-3 py-2 text-sm last:border-b-0"
                            data-testid={`moderation-row-${item.type}-${item.id}`}
                        >
                            <span className="truncate font-mono text-xs text-gray-500">
                                {item.id.slice(0, 8)}
                            </span>
                            <span>
                                <span className="inline-block rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-700">
                                    {TYPE_LABEL[item.type]}
                                </span>
                            </span>
                            <span className="truncate text-gray-900">{item.title}</span>
                            <span className="truncate text-gray-700">
                                {item.authorDisplayName}
                            </span>
                            <span className="text-xs text-gray-600">
                                {formatDate(item.createdAt)}
                            </span>
                            <span>
                                <span
                                    className={`inline-block rounded px-2 py-0.5 text-xs ${STATUS_COLOR[item.status]}`}
                                >
                                    {STATUS_LABEL[item.status]}
                                </span>
                            </span>
                            <span className="flex justify-end">
                                <button
                                    type="button"
                                    onClick={() => openItem(item.type, item.id)}
                                    data-testid={`moderation-open-${item.type}-${item.id}`}
                                    className="rounded border border-gray-300 px-3 py-1 text-xs font-medium text-gray-700 transition hover:bg-gray-100"
                                >
                                    Открыть
                                </button>
                            </span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
